import { find, partial, identity, words, capitalize, isArray, isEmpty} from 'lodash'
import * as Services from 'Services'
import { getSelected as _getSelected, getMaxPaintFillColors, getValidPaintColors, getAllSolidPaintColors, getAllPaintColors, getAllTranslucentPaintColors, getTranslucentPaintRules } from 'state/customize/selectors'

import { getPaintSummaryVM } from 'state/viewModels'
import { getPartPrice } from 'state/customize/selectors/getPrice'
import { colorKeyLookup } from 'state/customize'


function getSelected(state, key){
  var o = _getSelected(state,key);
  return (o) ? alterPartPrice(state, key, o) : o;
}

function alterPartPrice(state, key, option){
  return {
    ...option,
    price: getPartPrice(state, key, null, option),
  }
}

function alterPartPrices(state, key, opts){
  if (isArray(opts)) {
    if (opts[0] && opts[0].price !== undefined) {
      opts = opts.map((o) => (alterPartPrice(state, key, o))) 
    }
  }
  return opts; 
}

export function getPaintSummaryProps(state){
  return{ 
    paintSummary : getPaintSummaryVM(state),
    maxPaintfillColors : getMaxPaintFillColors(state),
    allPaintColors : getAllPaintColors(state),
    paintSwapValid : state.paintSwapValid,
  } 
}

export function isPaintfillArea(locationParam) {
  return !(/text/i).exec(locationParam) &&
    !(/stamp/i).exec(locationParam) &&
    !(/topline/i).exec(locationParam) &&
    !(/flangeline/i).exec(locationParam)
    ;
}

export function getPaintVM(state, parameterKey, noWarn) {
  let loc = find(state.putter.paintFillLocation, { 'parameterKey' : 'topLinePerpendicularColor' });
  let hasPerpendicularLines = false;
  if(loc && (parameterKey.includes('topLine') || parameterKey.includes('sightDot')))
  {
    //if the putter has top perpendicluar lines then we use the top perpendicular line parameter 
    //in place of the topLines to colorize them altogether
    hasPerpendicularLines = true;
  }
  else 
  {
    loc = find(state.putter.paintFillLocation, { parameterKey });
  }
  const finish = getSelected(state, 'finish');
  let paints = [];
  let translucentPaints = [];
  const translucentPaintRules = getTranslucentPaintRules(state, finish, parameterKey);

  if(!translucentPaintRules.allowTranslucent)
  {
    if(!!loc)
    {
      paints = getValidPaintColors(state, loc.id);
    }
    else
    {
      paints = getAllSolidPaintColors(state);
    }
  }
  else
  {
    paints = getAllPaintColors(state);
  }

  translucentPaints = getAllTranslucentPaintColors(state);

  if(parameterKey.toLowerCase().indexOf('weight') === -1)
  {
    //for non-weight locations, show only non 'onWeightsOnly' paint colors
    paints = paints.filter(paint => paint.onWeightsOnly === 'False');
  }
  //For non-cavity dots filter out paint colors that are for cavity dots only
  if(loc && !loc.isCavityDot)
  {
    paints = paints.filter(paint => paint.onCavityDotsOnly === 'False');
  }
  const selections = getSelected(state, 'paintFillLocations') || [];
  let selected = find(selections, { location: { parameterKey } });
  if(hasPerpendicularLines && parameterKey.includes('topLine'))
  {
    selected = find(selections, { location: { 'parameterKey' : 'topLinePerpendicularColor' } });
  }
  selected = (selected && selected.color) || {};

  if (!loc && !noWarn) {
    console.warn(`Couldn't find paintfill location for parameter ${parameterKey}`);
  }

  return (loc) ? { loc, paints, translucentPaints, selected } : null;
}

export function getInitialsVM(state, initialsLoc) {
  const loc = initialsLoc;
  const styleOptions = state.putter.personalizationStyle || [];
  const selections = getSelected(state, 'initials') || [];
  const selected = selections[initialsLoc.label] || {};

  //Link real text color and archTextColor paint params
  const paintKey = initialsLoc.paintFillKey
  let paint = getPaintVM(state, paintKey);
  if (paintKey === 'faceTextColor'){
    const archPaint = getPaintVM(state, 'archTextColor', true);
    if (archPaint){ 
      paint = mergePaintVMs( [paint, archPaint ]);
    }
  }

  return { loc, styleOptions, selected, paint };
}

export function getStampVM(state, stampLoc) {
  const loc = stampLoc;
  let stamps = state.putter.stamp || [];
  const selections = getSelected(state, 'stampSelections') || [];
  let selected = find(selections, { locationId: stampLoc.id });
  const paint = getPaintVM(state, stampLoc.paintFillKey);

  if(stampLoc.parameterKey != "toeStamp") {
	stamps = stamps.filter((stamp) => !stamp.faceToeOnly);
  }	  

  return { loc, stamps, selected, paint };
}

export function mergePaintVMs(paints){
  if (!paints.filter(identity).length) return {};
  const ids = paints.map(p => p.loc.id); 
  const label = paints[0].loc.label.replace('Toe','').replace('Heel','');
  const loc = Object.assign({}, paints[0].loc, {id:ids, label});
  return Object.assign({}, paints[0],  {loc}) 
}

export function mergeLinePaintVMs(paints) {
  const unMergedPaints = paints.filter(paint => !paint.loc.label.includes('Toe') && !paint.loc.label.includes('Heel'));
  const mergedPaint = mergePaintVMs(paints.filter(paint => paint.loc.label.includes('Toe') || paint.loc.label.includes('Heel')));
  if(!isEmpty(mergedPaint))
    return [...unMergedPaints, mergedPaint];
  return unMergedPaints;
}

export function getLineVM(state, optionType) {
  const options = state.putter[optionType];
  const selected = getSelected(state, optionType);
  const paints = ((selected && selected.parameterKeys.split(',')) || [])
    .filter(identity)
    .map(k => colorKeyLookup[k])
    .map(partial(getPaintVM, state))
    ;
  return { 
    options, 
    selected, 
    paints: mergeLinePaintVMs(paints),
  }
}

export function getEngravingVM(state, parameterKey) {
  const stampLoc = Object.assign({},
    state.putter.weightEngraving,
    {
      label: words(parameterKey).map(capitalize).join(' '),
      parameterKey,
    });
  const loc = stampLoc;
  const stamps = state.putter.weightEngravingStamp || [];
  const translucentStamps = stamps.filter((stamp) => stamp.allowTranslucentEngraving);
  const selected = getSelected(state, parameterKey) || {};

  return { loc, stamps, translucentStamps, selected };
}

export function getInsuranceVM(state){
  return {
    selected : state.instance.insuranceValue
  } 
}

export function getWeightVM(state, isTop) {
  const keys = {
    weight: isTop ? 'topWeight' : 'weight',
    weightEngraving: isTop ? 'topWeightEngraving' : 'weightEngraving',
    weightEngravingHeel: isTop ? 'weightEngravingTopHeel' : 'weightEngravingHeel',
    weightEngravingToe: isTop ? 'weightEngravingTopToe' : 'weightEngravingToe',
    weightFrontColor: isTop ? 'topWeightFrontColor' : 'weightFrontColor',
    weightRearColor: isTop ? 'topWeightRearColor' : 'weightRearColor'
  }
  const weights = state.putter[keys.weight];
  const selected = getSelected(state, keys.weight);
  const selectedEngraving = getSelected(state, keys.weightEngraving)
  const engravingOption = (state.putter[keys.weightEngraving] && state.putter[keys.weightEngraving][0]) || {} 
  const engravingOption2 = (state.putter[keys.weightEngraving] && state.putter[keys.weightEngraving][1]) || {} 
  const frontEngraving = getEngravingVM(state, keys.weightEngravingToe);
  const rearEngraving = getEngravingVM(state, keys.weightEngravingHeel); 
  const frontPaint = getPaintVM(state, keys.weightFrontColor);
  const rearPaint = getPaintVM(state, keys.weightRearColor);

  return { keys, weights, selected, engravingOption, engravingOption2, selectedEngraving, frontEngraving, rearEngraving, frontPaint, rearPaint };
}

export function getOptionVM(state, key, usePriceFromData) {
  return {
    options: (usePriceFromData) ? state.putter[key] : alterPartPrices(state, key, state.putter[key]),
    selected: getSelected(state, key) 
  }
}

export function getDefaultColors(state) {
  let defaultPaintFillLocs = state.defaultInstance.paintFillLocations;
  let defaultColors = [];
  for(let y = 0; y < defaultPaintFillLocs.length; y++) {
	  let loc = defaultPaintFillLocs[y];
	  
	  if(loc.colorLabel != 'Raw' && !defaultColors.includes(loc.colorId)) {
		  defaultColors.push(loc.colorId);
	  }
  }
  
  return defaultColors;
}

export function getDefaultLocationsForColor(state, colorId) {
  let defaultPaintFillLocs = state.defaultInstance.paintFillLocations;
  let defaultLocs = [];
  for(let y = 0; y < defaultPaintFillLocs.length; y++) {
	  let loc = defaultPaintFillLocs[y];
	  
	  if(loc.colorId == colorId && !defaultLocs.includes(loc.locationId)) {
		  defaultLocs.push(loc.locationId);
	  }
  }
  
  return defaultLocs;
}

export function getPaletteColors(state) {
  let paints = [];
  if(typeof state.instance['paletteColors'] !== 'undefined' && state.instance['paletteColors'] ) {
	paints = state.instance['paletteColors'];
  }
  
  if(paints.length > 0 && (typeof paints[0] == "undefined" || paints[0] == null)) {
	  paints = [];
  }
  
  return paints;
}


export function getPaintFillPrice() {
  return '78.00';
}

export function getCookieVal(cname) {
  var name = cname + "=";
  var decodedCookie = decodeURIComponent(document.cookie);
  var ca = decodedCookie.split(';');
  for(var i = 0; i <ca.length; i++) {
    var c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
}

function setCookieVal(name, value, ms, path, domain) {
	if (!name || !value) {
	  return;
	}
	var d;
	var cpath = path ? '; path=' + path : '';
	var cdomain = domain ? '; domain=' + domain : '';
	var expires = '';
	if (ms) {
	  d = new Date();
	  d.setTime(d.getTime() + ms);
	  expires = '; expires=' + d.toUTCString();
	}
	document.cookie = name + "=" + value + expires + cpath + cdomain;
}

export function setPutterDisclaimerCookie() {
  if(getCookieVal('PutterDisclaimerShown') == '1')
	  return;
  
  return setCookieVal('PutterDisclaimerShown', '1', false, false, false);
}

export function hasSeenPutterDisclaimer() {
  return getCookieVal('PutterDisclaimerShown') == '1';
}